import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Form, Input, Button } from 'antd'
import { useToast } from '../../../helpers/commonFunction'
import { Http } from '../../../helpers/axois'
import { Upload } from './Upload'
import { UploadScreenshots } from './UploadScreenshots'
import { getFilename, validateRule, deleteImage } from './helpers'
import { createAppRule } from './constants'
import { IScreenshot } from './type'
import { AppState } from '../../../stateManager/store'
import style from './styles/marketplace-apps.module.css'

// icons
import { ReactComponent as ArrowLeftIcon } from '../../../assets/svg/layer-left.svg'

interface ICreateApp {
  edit?: boolean
  editData?: any
}

interface IState {
  name: string
  currentVersion: string
  description: string
  authorWebsite: string
  supportEmail: string
  avatar: string
  objectUrl: string
  releaseNotes: string
  screenshots: string[]
  type: string;
}

export const CreateApp: React.FC<ICreateApp> = ({ edit, editData }) => {
  const orgId = useSelector((state: AppState) => state.org.selectedOrg?.id)
  const history = useHistory()
  const { SuccessToast, ErrorToast } = useToast()
  const [loading, setLoading] = useState(false)
  const [screenshots, setScreenshots] = useState<IScreenshot[]>([])
  const versionRegex = new RegExp("^[0-9]+.[0-9]+.[0-9]+$");
  const [isValidVersion, setIsValidVersion] = useState<boolean>(false);
  const orgPermissions: any = useSelector((state: any) => {
    return state.org.orgPermissions
}, (oldPermissions: any, newPermissions: any) => JSON.stringify(oldPermissions) === JSON.stringify(newPermissions))
const { selectedOrg } = useSelector((state: AppState) => state.org)
const loggedInUserId = useSelector((state: any) => {
    return state.user.data.user.id
})
  const [app, setApp] = useState<IState>({
    name: '',
    currentVersion: '',
    description: '',
    authorWebsite: '',
    supportEmail: '',
    avatar: '',
    objectUrl: '',
    releaseNotes: '',
    screenshots: [],
    type: "",
  })

  useEffect(() => {
    if (editData) {
      setApp(editData)
      setScreenshots(editData.screenshots.map((el: string) => ({
        loading: false,
        name: getFilename(el),
        url: el,
      })))
    }
  }, [editData])

  useEffect(() => {
    if (screenshots) {
      setApp((prevState) => ({
        ...prevState,
        screenshots: screenshots.map((el) => el.url),
      }))
    }
  }, [screenshots])

  const handleCreate = async () => {
    if ((!orgPermissions?.includes("marketplace.PublishApp") || !orgPermissions.includes('org.View')) && loggedInUserId!=selectedOrg?.owner) {
      ErrorToast("Not Authorized: Please request owner of this organization to provide access")
      return
    }
    try {
      const errorMsg = validateRule(createAppRule, app)
      if(!errorMsg) {
        await Http.post(`/marketplaceApp?orgId=${orgId}`, app).toPromise()
        SuccessToast('Successfully created app')
        return
      }
      throw new Error(errorMsg)
    } catch (err) {
      var msg = err?.message || 'Failed to create app'
      if (err === "Forbidden") {
        msg = "Not Authorized: Please request owner of this organization to provide access"
      }
      throw new Error(msg)
    }
  }

  const handleEdit = async () => {
    if ((!orgPermissions?.includes("marketplace.PublishApp") ||!orgPermissions.includes('org.View'))&& loggedInUserId!=selectedOrg?.owner) {
      ErrorToast("Not Authorized: Please request owner of this organization to provide access")
      return
    }
    const { id: appId } = editData
    const { description, authorWebsite, supportEmail, screenshots, avatar } =
      app

    const payload = {
      description,
      authorWebsite,
      supportEmail,
      avatar,
      screenshots,
    }

    try {
      const errMsg = validateRule(createAppRule, payload)
      if(!errMsg) {
        await Http.put(`/marketplaceApps/${appId}?orgId=${orgId}`, payload).toPromise()
        SuccessToast('Successfully updated app')
        return
      }
      throw new Error(errMsg)
    } catch (err) {
      var msg = err?.message || 'Failed to update app'
      if (err === "Forbidden") {
        msg = "Not Authorized: Please request owner of this organization to provide access"
      }
      throw new Error(msg)
    }
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      edit ? await handleEdit() : await handleCreate()
      history.push('/marketplace/apps')
    } catch (err) {
      ErrorToast(err?.message || err)
    } finally {
      setLoading(false)
    }
  }

  const updateApp = (key: string, value: string) => {
    setApp((prevState) => ({ ...prevState, [key]: value }))
  }

  // helper functions for maintaining screenshots state
  const setScreenshotLoadingStatus = (url: string, loading: boolean) => {
    setScreenshots((prevState) =>
      prevState.map((el) => (el.url === url ? { ...el, loading } : el))
    )
  }

  const removeScreenshot = async (url: string) => {
    setScreenshotLoadingStatus(url, true)
    try {
      if ((!orgPermissions?.includes("marketplace.PublishApp") || !orgPermissions.includes('org.View'))&& loggedInUserId!=selectedOrg?.owner) {
        ErrorToast("Not Authorized: Please request owner of this organization to provide access")
        return
      }
      await deleteImage(url, orgId)
      setScreenshots((prevState: any) => prevState.filter((el: any) => el.url !== url))
    } catch (err) {
      ErrorToast(err?.message || err)
      setScreenshotLoadingStatus(url, false)
    }
  }
  const handleOnBlur = () => {
    if (!versionRegex.test(app.currentVersion)) {
      setIsValidVersion(true)
    } else {
      setIsValidVersion(false)
    }
  }
  // === xxx ===

  return (
    <div className="create-marketplace-app">
      <Header
        loading={loading}
        handleSubmit={handleSubmit}
        edit={Boolean(edit)}
        appName={app.name}
        isValidVersion={isValidVersion}
      />
      <Form layout="horizontal" style={{ padding: '16px 24px' }}>
        <Form.Item label="Name" required={true}>
          <Input
            disabled={Boolean(edit)}
            placeholder="Enter name"
            value={app.name}
            onChange={(e) => updateApp('name', e.target.value)}
          />
        </Form.Item>

        <Form.Item label="Description" required={true}>
          <Input.TextArea
            rows={4}
            value={app.description}
            onChange={(e) => updateApp('description', e.target.value)}
          />
        </Form.Item>

        <Upload
          type="THUMBNAIL"
          url={app.avatar}
          setState={(avatar: string) => updateApp('avatar', avatar)}
        />

        <UploadScreenshots
          screenshots={screenshots}
          setScreenshots={setScreenshots}
          removeScreenshot={removeScreenshot}
        />

        <Upload
          type="APP"
          url={app.objectUrl}
          setState={(objectUrl: string) => updateApp('objectUrl', objectUrl)}
          setType={(type: string) => updateApp('type', type)}
          edit={Boolean(edit)}
        />

        <div className={style.formGroup}>
          <Form.Item label="Website" className="col-md-6">
            <Input
              placeholder="Enter website"
              value={app.authorWebsite}
              onChange={(e) => updateApp('authorWebsite', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Support Email" className="col-md-6">
            <Input
              placeholder="Enter email"
              value={app.supportEmail}
              onChange={(e) => updateApp('supportEmail', e.target.value)}
            />
          </Form.Item>
        </div>

        <div className={style.formGroup}>
          <Form.Item label="Release Notes" className="col-md-6">
            <Input.TextArea
              disabled={Boolean(edit)}
              value={app.releaseNotes}
              onChange={(e) => updateApp('releaseNotes', e.target.value)}
              rows={1}
            />
          </Form.Item>
          <Form.Item label="Version" className="col-md-6" required={true}>
            <Input
              disabled={Boolean(edit)}
              placeholder="Enter version"
              value={app.currentVersion}
              onChange={(e) => updateApp('currentVersion', e.target.value)}
              onBlur={handleOnBlur}
            />
            {isValidVersion &&
              <div className="text-danger p-2 mt-3">Enter a valid version number</div>
            }
          </Form.Item>
        </div>
      </Form>
    </div>
  )
}

interface IHeader {
  handleSubmit: () => void
  loading: boolean
  edit: boolean
  appName: string
  isValidVersion: boolean
}

const Header: React.FC<IHeader> = ({
  handleSubmit,
  loading,
  edit,
  appName,
  isValidVersion,
}) => {
  const history = useHistory()
  return (
    <div className={style.header}>
      <h4>
        <ArrowLeftIcon onClick={() => history.push('/marketplace/apps')} />{' '}
        {edit ? appName : 'Create App'}
      </h4>
      <Button shape="round" onClick={handleSubmit} disabled={isValidVersion} loading={loading}>
        {edit ? 'Update' : 'Submit'}
      </Button>
    </div>
  )
}
